"use client";

import Link from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import {
  ACTIVE_INDICATOR_STYLE,
  HOVER_BACKGROUND_STYLE,
  HOVER_BACKGROUND_TOP,
  TAB_ACTIVE_COLOR,
  TAB_COUNT_BADGE_STYLE,
  TAB_HEIGHT,
  TAB_INACTIVE_COLOR,
  TAB_PADDING_X,
  TAB_PADDING_Y,
  TAB_TEXT_STYLE,
  TAB_TRANSITION,
} from "@/components/app/horizontal-tabs-styles";
import { cn } from "@/lib/utils";

type TabId =
  | "leaderboard"
  | "exam"
  | "community"
  | "contribute"
  | "methodology";

/**
 * The segment a tab owns, relative to `/assessments/[slug]`.
 *
 * The board is the index, so its segment is empty and `useSelectedLayoutSegment`
 * reports Next's own `__PAGE__` sentinel for it rather than a name.
 */
const INDEX_SEGMENT = "__PAGE__";

const TABS: { id: TabId; label: string; segment: string }[] = [
  { id: "leaderboard", label: "Leaderboard", segment: "" },
  { id: "exam", label: "Exam", segment: "exam" },
  { id: "community", label: "Community", segment: "community" },
  { id: "contribute", label: "Contribute", segment: "contribute" },
  { id: "methodology", label: "Methodology", segment: "methodology" },
];

interface AssessmentTabsProps {
  slug: string;
  /** Rows on the published board. Badged on Leaderboard; hidden at zero. */
  boardCount?: number;
  /** People who have sat the exam. Badged on Community; hidden at zero. */
  cohortCount?: number;
}

/**
 * The platform's tab strip, driven by the route rather than by state.
 *
 * `HorizontalTabs` itself cannot be reused: it is wired to organization scopes,
 * tRPC and the study/project tab configs, none of which exist on a public page
 * that must render for a logged-out visitor. This shares its styles and its
 * behaviour instead, so a change to the platform's tabs reaches this page too.
 *
 * Both the hover background and the active underline are single elements that
 * slide, rather than borders on each tab. That is what produces the movement
 * between tabs; drawing them per-tab would make them appear and disappear
 * instead.
 *
 * One route per tab rather than one page holding five panels. Every tab here is
 * substance a search engine should have -- the brief, the published risk list,
 * what the gates are -- and a panel that only exists after a click is substance
 * behind an interaction. Five URLs also means a link can point at the part of an
 * assessment somebody is actually talking about.
 */
export function AssessmentTabs({
  slug,
  boardCount,
  cohortCount,
}: AssessmentTabsProps) {
  const segment = useSelectedLayoutSegment();
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverStyle, setHoverStyle] = useState({ left: "0px", width: "0px" });
  const [activeStyle, setActiveStyle] = useState({ left: "0px", width: "0px" });

  const active = segment === INDEX_SEGMENT || segment === null ? "" : segment;
  const activeIndex = Math.max(
    TABS.findIndex((tab) => tab.segment === active),
    0,
  );

  useEffect(() => {
    if (hoveredIndex === null) {
      return;
    }
    const element = tabRefs.current[hoveredIndex];
    if (element) {
      setHoverStyle({
        left: `${String(element.offsetLeft)}px`,
        width: `${String(element.offsetWidth)}px`,
      });
    }
  }, [hoveredIndex]);

  useEffect(() => {
    const element = tabRefs.current[activeIndex];
    if (!element) {
      return;
    }

    // Measured on the next frame: on first paint the fonts may not have
    // settled, and an underline sized against fallback metrics lands visibly
    // off the label it belongs to.
    const frame = requestAnimationFrame(() => {
      setActiveStyle({
        left: `${String(element.offsetLeft)}px`,
        width: `${String(element.offsetWidth)}px`,
      });
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [activeIndex]);

  return (
    // `min-w-0` because this is a flex child: a flex item defaults to
    // `min-width: auto`, so without it the strip refuses to shrink below its
    // five tabs and pushes the page into a horizontal scroll on a phone rather
    // than scrolling inside itself.
    <nav aria-label="Assessment sections" className="relative min-w-0">
      <div className="scrollbar-hide relative flex flex-nowrap items-center space-x-[6px] overflow-x-auto pb-0.5">
        <div
          className={HOVER_BACKGROUND_STYLE}
          style={{
            ...hoverStyle,
            top: HOVER_BACKGROUND_TOP,
            opacity: hoveredIndex === null ? 0 : 1,
          }}
        />

        <div className={ACTIVE_INDICATOR_STYLE} style={activeStyle} />

        {TABS.map((tab, index) => {
          const isActive = index === activeIndex;
          const count =
            tab.id === "leaderboard"
              ? boardCount
              : tab.id === "community"
                ? cohortCount
                : undefined;

          return (
            <Link
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "relative z-10 flex shrink-0 cursor-pointer items-center",
                TAB_HEIGHT,
                TAB_PADDING_X,
                TAB_PADDING_Y,
                TAB_TRANSITION,
                isActive ? TAB_ACTIVE_COLOR : TAB_INACTIVE_COLOR,
              )}
              href={`/assessments/${slug}${tab.segment ? `/${tab.segment}` : ""}`}
              key={tab.id}
              onMouseEnter={() => {
                setHoveredIndex(index);
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
              }}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
            >
              <span className={TAB_TEXT_STYLE}>{tab.label}</span>
              {count ? (
                <span className={cn(TAB_COUNT_BADGE_STYLE, "tabular-nums")}>
                  {count}
                </span>
              ) : null}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
